/**
 * Ticket collaboration client.
 *
 * Talks to the authenticated app API (`/api/tickets/*`, served by
 * `mttr/app_api/routes/tickets.py` — the standalone :8089 ticket_api was
 * retired and folded in). Same-origin relative paths; every call carries the
 * session token. All calls go through a single helper so error shapes stay
 * consistent.
 */
import { ApiError, apiBase, apiFetch } from "./apiClient";

/** Metadata for a photo attached to a comment (migration 176). The image
 *  itself is fetched separately with `fetchTicketPhotoUrl`. */
export interface TicketPhoto {
  photo_id: number;
  caption: string | null;
  /** Troubleshooting step it was taken at; null from the plain comment form. */
  node_id: string | null;
  uploaded_by: string;
  uploaded_by_uid: number | null;
  size_bytes: number;
}

export interface TicketComment {
  comment_id: number;
  author: string;
  posted_utc: string;
  work_type: "remote" | "onsite" | "note" | "status";
  body: string;
  /** Photos this comment placed in the stream. Absent from older servers. */
  photos?: TicketPhoto[];
}

export interface CommentsResponse {
  ticket_origin: "source" | "_jcox";
  ticket_id: number;
  comments: TicketComment[];
}

export interface AssigneeResponse {
  ticket_origin: "source" | "_jcox";
  ticket_id: number;
  assignee: string | null;
  assigned_utc: string | null;
}

export class TicketApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "TicketApiError";
  }
}

function ticketUrl(
  origin: "source" | "_jcox",
  ticketId: number,
  suffix: string,
): string {
  return `/api/tickets/${origin}/${ticketId}/${suffix}`;
}

async function jsonRequest<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  // These routes are now served by the AUTHENTICATED app API (folded in from
  // the retired :8089 ticket service), so every call must carry the session
  // token. The SPA persists it at localStorage["mttr_token"]; the backend
  // accepts it via the `access-token` header (see mttr/app_api/util.py).
  const token = localStorage.getItem("mttr_token");
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    if (e instanceof ApiError) {
      // Preserve the historical "<status>: <detail>" message shape.
      const msg = e.status != null ? `${e.status}: ${e.message}` : e.message;
      throw new TicketApiError(msg, e.status);
    }
    throw e;
  }
}

export async function fetchTicketComments(
  origin: "source" | "_jcox",
  ticketId: number,
): Promise<CommentsResponse> {
  return jsonRequest<CommentsResponse>(ticketUrl(origin, ticketId, "comments"));
}

export async function postTicketComment(
  origin: "source" | "_jcox",
  ticketId: number,
  payload: { author: string; work_type: "remote" | "onsite" | "note"; body: string },
): Promise<{ comment_id: number }> {
  return jsonRequest(ticketUrl(origin, ticketId, "comments"), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Attach a photo: the server writes a note comment + the photo together.
 *  `image` is a data URL (see utils/imageResize.ts). */
export async function uploadTicketPhoto(
  origin: "source" | "_jcox",
  ticketId: number,
  payload: {
    image: string;
    caption?: string;
    nodeId?: string;
    workType?: "remote" | "onsite" | "note";
  },
): Promise<{ comment_id: number; photo_id: number }> {
  return jsonRequest(ticketUrl(origin, ticketId, "photos"), {
    method: "POST",
    body: JSON.stringify({
      image: payload.image,
      caption: payload.caption || undefined,
      node_id: payload.nodeId || undefined,
      work_type: payload.workType,
    }),
  });
}

/**
 * Fetch one photo and return an object URL for an <img>.
 *
 * The photo route needs the session token in a header, which a plain
 * `<img src>` cannot send, so the bytes come through fetch(). The caller
 * owns the URL and must `URL.revokeObjectURL` it when done.
 */
export async function fetchTicketPhotoUrl(
  origin: "source" | "_jcox",
  ticketId: number,
  photoId: number,
): Promise<string> {
  const token = localStorage.getItem("mttr_token");
  const res = await fetch(
    `${apiBase()}${ticketUrl(origin, ticketId, `photos/${photoId}`)}`,
    { headers: token ? { "access-token": token } : {}, cache: "no-store" },
  );
  if (!res.ok) throw new TicketApiError(`${res.status}: photo unavailable`, res.status);
  return URL.createObjectURL(await res.blob());
}

/** Remove a photo from the activity view (uploader or admin only). */
export async function deleteTicketPhoto(
  origin: "source" | "_jcox",
  ticketId: number,
  photoId: number,
): Promise<{ photo_id: number; deleted: boolean }> {
  return jsonRequest(ticketUrl(origin, ticketId, `photos/${photoId}`), {
    method: "DELETE",
  });
}

export async function fetchTicketAssignee(
  origin: "source" | "_jcox",
  ticketId: number,
): Promise<AssigneeResponse> {
  return jsonRequest<AssigneeResponse>(ticketUrl(origin, ticketId, "assignee"));
}

export async function claimTicket(
  origin: "source" | "_jcox",
  ticketId: number,
  assignee: string,
): Promise<void> {
  await jsonRequest(ticketUrl(origin, ticketId, "claim"), {
    method: "POST",
    body: JSON.stringify({ assignee }),
  });
}

export async function releaseTicket(
  origin: "source" | "_jcox",
  ticketId: number,
  by: string,
): Promise<void> {
  await jsonRequest(ticketUrl(origin, ticketId, "release"), {
    method: "POST",
    body: JSON.stringify({ by }),
  });
}

/** Mark a SOURCE ticket as MTTR-resolved. Writes the overlay row only —
 *  source DB is untouched (CLAUDE.md hard rule #4). 409 for _jcox. */
export async function mttrResolveSourceTicket(
  ticketId: number,
  by: string,
  notes?: string,
): Promise<{ overlay_id: number }> {
  return jsonRequest(ticketUrl("source", ticketId, "mttr-resolve"), {
    method: "POST",
    body: JSON.stringify({ by, notes: notes ?? null }),
  });
}
